import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type BoardFixture, type HouseLineView, type MyBet } from '../../api'
import { Term } from '../Term'
import { actionButton } from '../ui/variants'
import { TeamBadge } from '../TeamBadge'
import { relativeTime } from '../../time'
import { marketLabel } from '../../markets'
import { type Route } from '../../route'
import { Leaderboard, MarketComparison } from './Standings'
import { SeasonSwitcher } from './SeasonSwitcher'
import { Tabs } from '../ui/Tabs'

type View = 'board' | 'bets' | 'leaderboard' | 'market' | 'season'

const VIEWS: { id: View; label: string }[] = [
  { id: 'board', label: 'Board' },
  { id: 'bets', label: 'Your bets' },
  { id: 'leaderboard', label: 'Leaderboard' },
  { id: 'market', label: 'At market prices' },
  { id: 'season', label: 'Season' },
]

/** "BO3" — the format reads as an abbreviation, so it is set like one. */
const formatLabel = (label: string) => label.toUpperCase()

/**
 * One season: what is bettable, what you bet, and where everyone stands.
 *
 * The sub-view rides in the fragment, so a leaderboard is a link somebody can paste into a chat
 * rather than a place you have to be talked to.
 */
export function SeasonBoard({ leagueId, seasonId, route, go }: {
  leagueId: number
  seasonId: number
  route: Route
  go: (...segments: (string | number)[]) => void
}) {
  const view = (VIEWS.find((entry) => entry.id === route.segments[2])?.id ?? 'board') as View

  const board = useQuery({
    queryKey: ['fantasy', 'board', leagueId, seasonId],
    queryFn: () => api.board(leagueId, seasonId),
    // Lines appear as fixtures are priced, and markets close when maps begin. A page left open on a
    // tournament night goes stale in minutes.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })

  const queryClient = useQueryClient()

  const join = useMutation({
    mutationFn: () => api.joinSeason(leagueId, seasonId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['fantasy'] }),
  })

  if (board.isPending) {
    return <p className="py-12 text-center text-sm text-ink-faint">Loading…</p>
  }

  if (board.error) {
    return (
      <p className="rounded border border-danger-edge bg-danger-bg px-4 py-3 text-sm text-danger">
        {board.error instanceof Error ? board.error.message : 'Could not load this season.'}
      </p>
    )
  }

  const data = board.data

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <SeasonSwitcher leagueId={leagueId} seasonId={seasonId} go={go} />
        {data.bankroll !== null ? (
          <div className="text-right">
            <div className="tabular text-2xl text-accent">
              {data.bankroll.available.toLocaleString()} gold
            </div>
            {/* The secondary line carries what is committed, the way a fixture card carries its
                post-draft number under the pre-draft one: the headline is the figure you act on,
                and the other is there when it has something to say. */}
            <div className="tabular text-xs text-ink-faint">
              {data.bankroll.atRisk > 0
                ? `${data.bankroll.atRisk.toLocaleString()} gold riding on ${data.bankroll.open} ${data.bankroll.open === 1 ? 'bet' : 'bets'}`
                : 'nothing riding'}
            </div>
          </div>
        ) : null}
      </div>

      <Tabs
        ariaLabel="Season views"
        active={view}
        onChange={(next) => go(leagueId, seasonId, next)}
        tabs={VIEWS.map((entry) => ({
          key: entry.id,
          label: entry.label,
          // No badge here any more. It counted OPEN bets and sat on a tab whose list shows every
          // bet, so "Your bets 1" led to three of them — and the header already says "1,000 gold
          // riding on 1 bet" two inches away, unambiguously and in words. One of the two had to go,
          // and the one that could be misread is the one that went.
          badge: undefined,
        }))}
      />

      {!data.entered ? (
        <div className="min-w-0 rounded-lg border border-accent-edge bg-accent-bg p-4">
          <p className="text-sm">
            You are not in this season yet. Everyone starts with the same{' '}
            <Term id="bankroll">bankroll</Term>, whenever they join.
          </p>
          <button
            type="button"
            onClick={() => join.mutate()}
            disabled={join.isPending || data.season.status !== 'open'}
            className={actionButton({ className: 'mt-3' })}
          >
            {data.season.status === 'open' ? 'Join and get 10,000 gold' : 'Not open yet'}
          </button>
        </div>
      ) : null}

      {view === 'board' ? (
        <BoardView
          leagueId={leagueId}
          seasonId={seasonId}
          fixtures={data.fixtures}
          canBet={data.entered && data.season.status === 'open'}
          available={data.bankroll?.available ?? 0}
          maxPerBet={data.season.maxStakePerBet}
          maxPerFixture={data.season.maxStakePerFixture}
        />
      ) : null}
      {view === 'bets' ? (
        <MyBetsView leagueId={leagueId} seasonId={seasonId} />
      ) : null}
      {view === 'leaderboard' ? <Leaderboard leagueId={leagueId} seasonId={seasonId} /> : null}
      {view === 'market' ? <MarketComparison leagueId={leagueId} seasonId={seasonId} /> : null}
      {view === 'season' ? <SeasonDetailView leagueId={leagueId} seasonId={seasonId} /> : null}
    </div>
  )
}

function BoardView({ leagueId, seasonId, fixtures, canBet, available, maxPerBet, maxPerFixture }: {
  leagueId: number
  seasonId: number
  fixtures: BoardFixture[]
  canBet: boolean
  available: number
  maxPerBet: number
  maxPerFixture: number
}) {
  if (fixtures.length === 0) {
    return (
      <p className="rounded border border-line bg-surface px-4 py-8 text-center text-sm text-ink-soft">
        Nothing to bet on right now. Matches turn up here once the schedule lists them and the model
        can put a number on both line-ups.
      </p>
    )
  }

  return (
    <div className="space-y-3">
      <p className="max-w-3xl text-sm text-ink-soft">
        Each market shows the model's own chance for whichever team it favours. The house model
        predicts the following odds pre-draft. See if you can beat it — or your
        friends at the leaderboards. You can bet until game start, so feel free to take advantage of
        additional drafting information{' '}
        <span aria-hidden="true">;)</span>
      </p>

      <div className="grid gap-3 lg:grid-cols-2">
        {fixtures.map((fixture) => (
          <FixtureBoardCard
            key={fixture.fixtureId}
            leagueId={leagueId}
            seasonId={seasonId}
            fixture={fixture}
            canBet={canBet}
            available={available}
            maxPerBet={maxPerBet}
            maxPerFixture={maxPerFixture}
          />
        ))}
      </div>
    </div>
  )
}

function FixtureBoardCard({
  leagueId,
  seasonId,
  fixture,
  canBet,
  available,
  maxPerBet,
  maxPerFixture,
}: {
  leagueId: number
  seasonId: number
  fixture: BoardFixture
  canBet: boolean
  available: number
  maxPerBet: number
  maxPerFixture: number
}) {
  /*
   * What this fixture can still carry, counted across ALL of its markets.
   *
   * The series and its maps are close to the same bet — a side that wins the series usually wins two
   * of the maps — so the server counts them together, and a form that only knew the per-bet cap
   * would offer a number the server then quietly reduces. Summed from `yourBets`, which the board
   * already sends per market for the "You:" line underneath each price.
   */
  const openHere = fixture.markets.reduce(
    (total, market) => total + market.yourBets.reduce((sum, bet) => sum + bet.stake, 0),
    0,
  )

  const roomHere = Math.max(0, maxPerFixture - openHere)
  const live = fixture.score.mapsStarted > 0
  const drift =
    fixture.draftProbability !== null && fixture.preDraftProbability !== null
      ? fixture.draftProbability - fixture.preDraftProbability
      : null

  return (
    // min-w-0 on the card itself. A grid item defaults to a minimum of its content's width, and a
    // truncating child does not remove that contribution — which is what once blew this page open
    // sideways on every iPhone.
    <article className="min-w-0 rounded-lg border border-line bg-surface p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-1.5 text-sm">
            <TeamBadge name={fixture.teamAName} logo={fixture.teamALogo} size={22} />
            <span className="min-w-0 truncate">{fixture.teamAName}</span>
            <span className="shrink-0 text-ink-faint">vs</span>
            <TeamBadge name={fixture.teamBName} logo={fixture.teamBLogo} size={22} />
            <span className="min-w-0 truncate">{fixture.teamBName}</span>
          </div>
          <div className="truncate text-xs text-ink-faint">
            {live ? 'under way' : `starts ${relativeTime(fixture.startTime)}`} ·{' '}
            {formatLabel(fixture.seriesType)}
            {fixture.tournament !== null ? ` · ${fixture.tournament}` : ''}
          </div>
        </div>
        {live ? (
          <span className="tabular shrink-0 rounded bg-live-bg px-1.5 py-0.5 text-xs text-live">
            {fixture.score.a}–{fixture.score.b}
          </span>
        ) : null}
      </div>

      {drift !== null && Math.abs(drift) >= 0.02 ? (
        <p className="mt-2 rounded border border-accent-edge bg-accent-bg px-2 py-1 text-xs text-accent">
          The heroes are locked, and they move our number {drift > 0 ? 'up' : 'down'}{' '}
          {Math.abs(drift * 100).toFixed(1)} points for {fixture.teamAName}, to{' '}
          {(fixture.draftProbability! * 100).toFixed(0)}%. The prices below have not moved.
        </p>
      ) : null}

      <div className="mt-3 space-y-2">
        {fixture.markets.map((market) => (
          <MarketRow
            key={market.market}
            leagueId={leagueId}
            seasonId={seasonId}
            fixture={fixture}
            market={market}
            canBet={canBet}
            available={available}
            maxStake={Math.min(maxPerBet, roomHere, available)}
            maxPerFixture={maxPerFixture}
          />
        ))}
      </div>
    </article>
  )
}

function MarketRow({
  leagueId,
  seasonId,
  fixture,
  market,
  canBet,
  available,
  maxStake,
  maxPerFixture,
}: {
  leagueId: number
  seasonId: number
  fixture: BoardFixture
  market: HouseLineView
  canBet: boolean
  available: number
  /** The largest stake this particular bet may carry — the tightest of the three limits. */
  maxStake: number
  maxPerFixture: number
}) {
  const queryClient = useQueryClient()
  const [side, setSide] = useState<'A' | 'B' | null>(null)
  const [stake, setStake] = useState('100')
  const [error, setError] = useState<string | null>(null)

  const place = useMutation({
    mutationFn: () =>
      api.placeBet(leagueId, seasonId, {
        fixtureId: fixture.fixtureId,
        market: market.market,
        side: side!,
        stake: Number.parseInt(stake, 10),
        houseLineId: market.houseLineId,
      }),
    onSuccess: () => {
      setSide(null)
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['fantasy'] })
    },
    onError: (failure: Error) => setError(failure.message),
  })

  const closed = market.closed !== null
  const label = marketLabel(market.market)

  // Whichever side the model actually favours — that is the interesting half of the sentence, and
  // always naming team A would make it read as a coin-flip statement half the time.
  const favoured = market.sideAProbability >= 0.5
  const favouredTeam = favoured ? fixture.teamAName : fixture.teamBName
  const favouredChance = favoured ? market.sideAProbability : 1 - market.sideAProbability

  return (
    <div className="min-w-0 rounded border border-line bg-surface-raised p-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* No side chip. Which team drew radiant is GAME information, of a piece with the
            heroes they picked, and it is removed here for the same reason it is removed from the
            series card: one fact out of that set, on a card about prices, is too little to be
            useful and enough to crowd the row. If it returns it returns whole, in a panel of its
            own — see todo.md. `teamAIsRadiant` is still on the wire and still correct. */}
        <span className="flex items-center gap-1.5 text-xs text-ink-soft">{label}</span>
        <span className="flex items-center gap-2">
          {/*
            The model's own number, as a figure rather than a sentence.

            It used to read "Dota Oracle predicts 53% for LGD Gaming." under every market of every
            card — around twenty times on one screen, differing only by a name and two digits. The
            repeated-prose check cannot see it, because each copy differs; a reader sees nothing but
            the repetition. The words were the same every time, so they belong once on the page, and
            the two things that change belong here.
          */}
          <span className="tabular text-xs text-ink-faint">
            {(favouredChance * 100).toFixed(0)}% {favouredTeam}
          </span>
          {closed ? <span className="text-xs text-ink-faint">· {market.closed}</span> : null}
        </span>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-2">
        <PriceButton
          team={fixture.teamAName}
          logo={fixture.teamALogo}
          odds={market.sideAOdds}
          selected={side === 'A'}
          disabled={closed || !canBet}
          onClick={() => setSide(side === 'A' ? null : 'A')}
        />
        <PriceButton
          team={fixture.teamBName}
          logo={fixture.teamBLogo}
          odds={market.sideBOdds}
          selected={side === 'B'}
          disabled={closed || !canBet}
          onClick={() => setSide(side === 'B' ? null : 'B')}
        />
      </div>

      <Backers backers={market.backers} />

      {market.yourBets.length > 0 ? (
        <div className="tabular mt-1 text-xs text-ink-faint">
          You:{' '}
          {market.yourBets
            .map(
              (bet) =>
                `${bet.stake} on ${bet.side === 'A' ? fixture.teamAName : fixture.teamBName} at ${bet.decimalOdds.toFixed(2)}`,
            )
            .join(' · ')}
        </div>
      ) : null}

      {side !== null ? (
        <div className="mt-2 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={stake}
              onChange={(event) => setStake(event.target.value.replace(/[^0-9]/g, ''))}
              inputMode="numeric"
              className="tabular min-h-11 w-24 rounded border border-line bg-surface px-2 text-sm text-ink"
            />
            <button
              type="button"
              onClick={() => place.mutate()}
              disabled={place.isPending || stake.length === 0 || Number.parseInt(stake, 10) <= 0}
              className={actionButton()}
            >
              Bet
            </button>
            <span className="tabular text-xs text-ink-faint">
              returns{' '}
              {Math.floor(
                Math.min(Number.parseInt(stake, 10) || 0, maxStake) *
                  (side === 'A' ? market.sideAOdds : market.sideBOdds),
              ).toLocaleString()}{' '}
              gold · {available.toLocaleString()} left
            </span>
          </div>
          {/*
            The cap, stated before the bet rather than discovered through it.

            An over-cap stake is REDUCED by the server, not refused — so without this the page would
            take 5,000, return a bet of 1,000, and leave the reader to work out why. The number shown
            is the tightest of the three limits that apply, which is why it is computed by the card
            rather than read straight off the season.
          */}
          {(Number.parseInt(stake, 10) || 0) > maxStake ? (
            <p className="text-xs text-warn">
              Reduced to <span className="tabular">{maxStake.toLocaleString()}</span> gold.{' '}
              <Term id="stake-cap">Why?</Term>
            </p>
          ) : (
            <p className="text-xs text-ink-faint">
              Up to <span className="tabular">{maxStake.toLocaleString()}</span> on this one ·{' '}
              <span className="tabular">{maxPerFixture.toLocaleString()}</span> across the match.{' '}
              <Term id="stake-cap">Why?</Term>
            </p>
          )}
          {error !== null ? <p className="text-xs text-danger">{error}</p> : null}
        </div>
      ) : null}

    </div>
  )
}

/**
 * Who else in the league is on each side.
 *
 * The feature that turns a leaderboard into an opponent. A table of standings says who is ahead and
 * nothing about who anybody is playing against; this says "four of us are on the favourite, and the
 * one ahead of you is not" — which is the thing worth arguing about before a game rather than after
 * it.
 *
 * Laid out in the same two columns as the prices directly above, so a name sits under the side it
 * backed and the split is legible without reading a word of it. Stake order comes from the server,
 * biggest first, so the loudest position leads.
 */
function Backers({ backers }: { backers: HouseLineView['backers'] }) {
  if (backers.length === 0) return null

  const sides = (['A', 'B'] as const).map((side) => backers.filter((b) => b.side === side))

  return (
    <div className="mt-1 grid grid-cols-2 gap-2 text-xs">
      {sides.map((side, at) => (
        <div key={at} className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
          {side.length === 0 ? null : (
            side.map((backer) => (
              <span
                key={backer.name}
                className={`truncate ${backer.isYou ? 'text-accent' : 'text-ink-faint'}`}
              >
                {backer.isYou ? 'you' : backer.name}
              </span>
            ))
          )}
        </div>
      ))}
    </div>
  )
}

function PriceButton({ team, logo, odds, selected, disabled, onClick }: {
  team: string
  logo: string | null
  odds: number
  selected: boolean
  disabled: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex min-h-11 min-w-0 items-center justify-between gap-2 rounded border px-2 text-left text-sm transition-colors ${
        selected ? 'border-accent bg-accent-bg text-accent' : 'border-line bg-surface'
      } disabled:opacity-40`}
    >
      <span className="flex min-w-0 items-center gap-1.5">
        <TeamBadge name={team} logo={logo} size={18} />
        <span className="min-w-0 truncate text-xs">{team}</span>
      </span>
      <span className="tabular shrink-0">{odds.toFixed(2)}</span>
    </button>
  )
}

/**
 * Your bets, and the ability to change your mind about them.
 *
 * Editable and cancellable right up to the horn — see the API, which explains why a real bookmaker
 * would not allow it and why this one does.
 */
function MyBetsView({ leagueId, seasonId }: { leagueId: number; seasonId: number }) {
  const { data, isPending } = useQuery({
    queryKey: ['fantasy', 'my-bets', leagueId, seasonId],
    queryFn: () => api.myBets(leagueId, seasonId),
    refetchInterval: 60_000,
  })

  if (isPending) return <p className="text-sm text-ink-faint">Loading…</p>
  if (!data?.entered) return <p className="text-sm text-ink-soft">You are not in this season yet.</p>
  if (data.bets.length === 0) {
    return <p className="text-sm text-ink-soft">Nothing riding yet.</p>
  }

  return (
    <div className="min-w-0 space-y-2">
      {data.bets.map((bet) => (
        <BetRow key={bet.id} bet={bet} leagueId={leagueId} seasonId={seasonId} />
      ))}
    </div>
  )
}

function BetRow({ bet, leagueId, seasonId }: {
  bet: MyBet
  leagueId: number
  seasonId: number
}) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [stake, setStake] = useState(String(bet.stake))
  const [error, setError] = useState<string | null>(null)

  const refresh = () => {
    setEditing(false)
    setError(null)
    void queryClient.invalidateQueries({ queryKey: ['fantasy'] })
  }

  const restake = useMutation({
    mutationFn: () => api.restake(leagueId, seasonId, bet.id, Number.parseInt(stake, 10)),
    onSuccess: refresh,
    onError: (failure: Error) => setError(failure.message),
  })

  const cancel = useMutation({
    mutationFn: () => api.cancelBet(leagueId, seasonId, bet.id),
    onSuccess: refresh,
    onError: (failure: Error) => setError(failure.message),
  })

  const team = bet.side === 'A' ? bet.fixture.teamAName : bet.fixture.teamBName
  const label = bet.market === 'series' ? 'series' : `map ${bet.market.slice(3)}`
  const changeable = bet.status === 'open' && !bet.fixture.started

  const tone: Record<MyBet['status'], string> = {
    open: 'text-pending',
    won: 'text-win',
    lost: 'text-loss',
    void: 'text-void',
  }

  return (
    <div className="min-w-0 rounded border border-line bg-surface p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="min-w-0 truncate text-sm">
          {team} <span className="text-ink-faint">to win the {label}</span>
        </span>
        <span className={`tabular text-sm ${tone[bet.status]}`}>
          {bet.status === 'open'
            ? `${bet.stake.toLocaleString()} gold at ${bet.decimalOdds.toFixed(2)}`
            : bet.status === 'void'
              ? `void · ${bet.stake.toLocaleString()} gold back`
              : `${(bet.returned ?? 0).toLocaleString()} gold back on ${bet.stake.toLocaleString()}`}
        </span>
      </div>

      <div className="truncate text-xs text-ink-faint">
        {bet.fixture.teamAName} vs {bet.fixture.teamBName} ·{' '}
        {bet.fixture.started ? 'under way' : `starts ${relativeTime(bet.fixture.startTime)}`}
      </div>

      {bet.voidReason !== null ? (
        <p className="mt-1 text-xs text-ink-faint">
          <Term id="void-bet">Void</Term>: {bet.voidReason}
        </p>
      ) : null}

      {changeable ? (
        editing ? (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <input
              value={stake}
              onChange={(event) => setStake(event.target.value.replace(/[^0-9]/g, ''))}
              inputMode="numeric"
              className="tabular min-h-11 w-24 rounded border border-line bg-surface-raised px-2 text-sm text-ink"
            />
            <button
              type="button"
              onClick={() => restake.mutate()}
              disabled={restake.isPending || stake.length === 0}
              className={actionButton()}
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(false)
                setStake(String(bet.stake))
              }}
              className="min-h-11 rounded px-2 text-xs text-ink-faint hover:text-ink"
            >
              Never mind
            </button>
          </div>
        ) : (
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
            <button type="button" onClick={() => setEditing(true)} className="text-ink-faint hover:text-ink">
              Change stake
            </button>
            <button
              type="button"
              onClick={() => cancel.mutate()}
              disabled={cancel.isPending}
              className="text-ink-faint hover:text-danger disabled:opacity-50"
            >
              Take it back
            </button>
            <span className="text-ink-faint">— until the game starts</span>
          </div>
        )
      ) : null}

      {error !== null ? <p className="mt-1 text-xs text-danger">{error}</p> : null}
    </div>
  )
}

/**
 * The season's own page: its tournaments, and what its window could not account for.
 *
 * The unattributed diagnostic is here rather than hidden, because a fixture attached to no
 * tournament is bettable nowhere — correct behaviour and a silent hole at the same time.
 */
function SeasonDetailView({ leagueId, seasonId }: { leagueId: number; seasonId: number }) {
  const queryClient = useQueryClient()
  const { data, isPending } = useQuery({
    queryKey: ['fantasy', 'season', leagueId, seasonId],
    queryFn: () => api.season(leagueId, seasonId),
  })

  const setStatus = useMutation({
    mutationFn: (status: string) => api.updateSeason(leagueId, seasonId, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['fantasy'] }),
  })

  if (isPending || data === undefined) return <p className="text-sm text-ink-faint">Loading…</p>

  return (
    <div className="min-w-0 space-y-4">
      <div className="min-w-0 rounded-lg border border-line bg-surface p-4">
        <h3 className="text-sm font-medium">Tournaments followed</h3>
        <ul className="mt-2 space-y-1 text-xs text-ink-soft">
          {data.tournaments.map((entry) => (
            <li key={entry.leagueId} className="truncate">
              {entry.name ?? `League ${entry.leagueId}`}
            </li>
          ))}
        </ul>
        <p className="tabular mt-2 text-xs text-ink-faint">
          {data.fixtures} fixtures in the window · {new Date(data.opensAt).toLocaleDateString()} →{' '}
          {new Date(data.closesAt).toLocaleDateString()}
        </p>
      </div>

      {data.unattributedFixtures.count > 0 ? (
        <div className="min-w-0 rounded-lg border border-warn-edge bg-warn-bg p-4">
          <h3 className="text-sm font-medium text-warn">
            {data.unattributedFixtures.count}{' '}
            {data.unattributedFixtures.count === 1 ? 'match' : 'matches'} could not be attributed to
            a tournament
          </h3>
          <p className="mt-1 text-xs text-warn">{data.unattributedFixtures.note}</p>
          <ul className="mt-2 space-y-1 text-xs text-ink-soft">
            {data.unattributedFixtures.fixtures.map((fixture) => (
              <li key={fixture.id} className="truncate">
                {fixture.teamAName} vs {fixture.teamBName}
                {fixture.tournament !== null ? ` · ${fixture.tournament}` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {data.role === 'manager' ? (
        <div className="min-w-0 rounded-lg border border-line bg-surface p-4">
          <h3 className="text-sm font-medium">Manager</h3>
          <div className="mt-2 flex flex-wrap gap-2">
            {data.status === 'draft' ? (
              <button
                type="button"
                onClick={() => setStatus.mutate('open')}
                className={actionButton()}
              >
                Open the season
              </button>
            ) : null}
            {data.status === 'open' ? (
              <button
                type="button"
                onClick={() => setStatus.mutate('finished')}
                className="min-h-11 rounded bg-surface-raised px-3 text-sm text-ink-soft hover:text-ink"
              >
                Finish the season
              </button>
            ) : null}
          </div>
          <p className="mt-2 text-xs text-ink-faint">
            Finishing is one-way: the results are known by then, so anything bet afterwards would be
            bet with hindsight. It is also what puts the season into the career table.
          </p>
        </div>
      ) : null}
    </div>
  )
}
